import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type { BranchesListQuery, BranchesRepository } from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface BranchOption {
  readonly value: string;
  readonly label: string;
}

export interface UseBranchOptionsResult {
  readonly options: readonly BranchOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated branch options for the `Combobox` async mode --
 * mirrors useCompanyOptions, with one addition: `accountId` scopes the list
 * for a staff caller (a client caller is always limited to their own
 * account by the backend regardless, so it's meaningless -- and should stay
 * omitted -- for them). Pass `undefined` while nothing is chosen yet, same
 * as the Create Asset form's Asset Type picker.
 */
export function useBranchOptions(
  repository: BranchesRepository | undefined,
  accountId?: string,
): UseBranchOptionsResult {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith("ar");
  const sortBy = isAr ? "nameAr" : "nameEn";

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const isEnabled =
    repository !== undefined && typeof repository.list === "function";

  const listQuery = (page: number): BranchesListQuery => ({
    page,
    limit: PAGE_SIZE,
    sort: "asc",
    sortBy,
    isActive: true,
    ...(search === undefined ? {} : { search }),
    ...(accountId === undefined ? {} : { accountId }),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.branches.list({
      limit: PAGE_SIZE,
      search,
      sort: "asc",
      sortBy,
      accountId,
      isActive: true,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: isEnabled,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((branch) => ({
          value: branch.id,
          label: isAr
            ? branch.nameAr || branch.nameEn
            : branch.nameEn || branch.nameAr,
        })),
      ),
    [query.data, isAr],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
